import { useMutation } from '@tanstack/react-query';
import {
  createMassiveArticles,
  createMassiveFormats,
  createMassiveProviderFormats,
} from '../../../../api/carteleriaApi';
import toast from 'react-hot-toast';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';

const chunkArray = (array, size) => {
  const chunks = [];

  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }

  return chunks;
};

const useCreateMassiveArticles = () => {
  const queryClient = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  const { data, mutateAsync, isLoading, error } = useMutation({
    mutationFn: async (body) => {
      const token = await getAccessTokenSilently();

      // Combinar new + edited para cada entidad (withOutChanges no necesita subirse)
      const articles = [...(body.newArticles || []), ...(body.editedArticles || [])];
      const formats = [...(body.newFormats || []), ...(body.editedFormats || [])];
      const providersFormats = [...(body.newProvidersFormats || []), ...(body.editedProvidersFormats || [])];

      const totalArticles = articles.length;
      const totalFormats = formats.length;
      const totalProvidersFormats = providersFormats.length;

      const batchSize = 100;
      const articleBatches = chunkArray(articles, batchSize);
      const formatBatches = chunkArray(formats, batchSize);
      const providerFormatBatches = chunkArray(providersFormats, batchSize);

      let processedArticles = 0;
      let processedFormats = 0;
      let processedProvidersFormats = 0;

      // Mapas acumulados entre fases para resolver códigos temporales → _id real
      const articleCodeMap = {};
      const formatCodeMap = {};

      const toastId = toast.loading(`Fase 1/3: subiendo artículos 0/${totalArticles}...`);

      try {
        // ── Fase 1: Artículos ─────────────────────────────────────────────────
        for (let i = 0; i < articleBatches.length; i++) {
          const result = await createMassiveArticles(token, { articles: articleBatches[i] });

          Object.assign(articleCodeMap, result.data?.articleCodeMap || {});
          processedArticles += articleBatches[i].length;

          toast.loading(`Fase 1/3: subiendo artículos ${processedArticles}/${totalArticles}...`, { id: toastId });
        }

        // ── Fase 2: Formatos ──────────────────────────────────────────────────
        for (let i = 0; i < formatBatches.length; i++) {
          toast.loading(`Fase 2/3: subiendo formatos ${processedFormats}/${totalFormats}...`, { id: toastId });

          const result = await createMassiveFormats(token, {
            formats: formatBatches[i],
            articleCodeMap,
          });

          Object.assign(formatCodeMap, result.data?.formatCodeMap || {});
          processedFormats += formatBatches[i].length;

          toast.loading(`Fase 2/3: subiendo formatos ${processedFormats}/${totalFormats}...`, { id: toastId });
        }

        // ── Fase 3: Proveedores ───────────────────────────────────────────────
        for (let i = 0; i < providerFormatBatches.length; i++) {
          toast.loading(`Fase 3/3: subiendo proveedores ${processedProvidersFormats}/${totalProvidersFormats}...`, {
            id: toastId,
          });

          await createMassiveProviderFormats(token, {
            providersFormats: providerFormatBatches[i],
            articleCodeMap,
            formatCodeMap,
          });

          processedProvidersFormats += providerFormatBatches[i].length;

          toast.loading(`Fase 3/3: subiendo proveedores ${processedProvidersFormats}/${totalProvidersFormats}...`, {
            id: toastId,
          });
        }

        toast.success(
          `Subida completada. Artículos: ${processedArticles}/${totalArticles}. Formatos: ${processedFormats}/${totalFormats}. Proveedores: ${processedProvidersFormats}/${totalProvidersFormats}.`,
          { id: toastId },
        );

        return { articleCodeMap, formatCodeMap };
      } catch (error) {
        toast.error(
          `Error en la subida. Artículos: ${processedArticles}/${totalArticles}. Formatos: ${processedFormats}/${totalFormats}. Proveedores: ${processedProvidersFormats}/${totalProvidersFormats}.`,
          { id: toastId },
        );
        throw error;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries('all-artes-finales-master');
      return data;
    },
    onError: (error) => {
      return error;
    },
  });

  return {
    data,
    mutateAsync,
    isLoading,
    error,
  };
};

export default useCreateMassiveArticles;
